import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  useGetAIReportQuestionnaire,
  usePostAIReportQuestionnaire,
} from 'services/personalised-ai-report/hooks';
import { getCurrentTimestamp, getTimezone } from 'lib/utils';
import ReportQuestion from './ReportQuestion';
import {
  QuestionType,
  ReportQuestion as ReportQuestionType,
  USER_ACTIVITY,
} from '../../../types';
import { PATH_ROUTES } from 'routes/path';
import Loader from 'components/Loader';
import { captureUserActivity } from 'services/profile/api';
import FinalReport from './FinalReport';
import { ENG_NONE_OF_ABOVE, ES_NONE_OF_ABOVE } from 'lib/constants';
import useAuthStore from 'store/auth';

export default function GeneratePersonalisedAIReportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const locale = useAuthStore((store) => store.locale);

  const [currentQA, setCurrentQA] = useState<ReportQuestionType>();
  const [selectedEngChoices, setSelectedEngChoices] = useState<string[]>([]);
  const [selectedEsChoices, setSelectedEsChoices] = useState<string[]>([]);
  const [otherValue, setOtherValue] = useState<string>('');
  const [userInput, setUserInput] = useState<string>('');
  const [isFinalMsg, setIsFinalMsg] = useState<boolean>(false);

  const { data: reportQuestion, isFetching } =
    useGetAIReportQuestionnaire('latest');

  useEffect(() => {
    if (reportQuestion) setCurrentQA(reportQuestion?.[0]);
  }, [reportQuestion]);

  const { mutate, isPending } = usePostAIReportQuestionnaire({
    onSuccess: (data: ReportQuestionType) => {
      captureUserActivity(USER_ACTIVITY.SCAN_AI_REPORT, {
        question_id: currentQA?.q_id,
      });

      setUserInput('');
      setSelectedEngChoices([]);
      setSelectedEsChoices([]);

      if (data) {
        setCurrentQA(data);
      }

      setIsFinalMsg(data?.path_type === 'final_msg');
    },
    onError: () => toast.error(t('failed_response_submit_msg')),
  });

  const onPostQuestionnaire = (
    eng_choices: string,
    spanish_choices: string
  ) => {
    mutate({
      q_id: currentQA?.q_id || '',
      answer_id: currentQA?.answer_id || '',
      eng_choices,
      spanish_choices,
      timestamp: getCurrentTimestamp(),
      timezone: getTimezone(),
      path_type: currentQA?.path_type || '',
    });
  };

  const onSubmit = async () => {
    let eng_choices: string = userInput;
    let spanish_choices: string = userInput;

    if (currentQA?.question_type === QuestionType.MULTI_SELECT) {
      if (selectedEngChoices.includes('Other')) {
        const englishChoices: any = [...selectedEngChoices].filter(
          (item) => item !== 'Other'
        );
        englishChoices.push({ Other: otherValue });
        eng_choices = JSON.stringify(englishChoices);

        const spanishChoices: any = [...selectedEsChoices].filter(
          (item) => item !== 'Otro'
        );
        spanishChoices.push({ Otro: otherValue });
        spanish_choices = JSON.stringify(spanishChoices);
      } else {
        eng_choices = JSON.stringify(selectedEngChoices);
        spanish_choices = JSON.stringify(selectedEsChoices);
      }
    }

    onPostQuestionnaire(eng_choices, spanish_choices);
  };

  const onSelectMultiChoices = useCallback(
    (checked: boolean, engChoice: string, spanishChoice: string) => {
      if (checked) {
        let englishChoices = [...selectedEngChoices].filter(
          (item) => item !== ENG_NONE_OF_ABOVE
        );
        let spanishChoices = [...selectedEsChoices].filter(
          (item) => item !== ES_NONE_OF_ABOVE
        );

        if (
          engChoice === ENG_NONE_OF_ABOVE ||
          spanishChoice === ES_NONE_OF_ABOVE
        ) {
          englishChoices = [engChoice];
          spanishChoices = [spanishChoice];
        } else {
          englishChoices.push(engChoice);
          spanishChoices.push(spanishChoice);
        }

        setSelectedEngChoices(englishChoices);
        setSelectedEsChoices(spanishChoices);
      } else {
        setSelectedEngChoices((prev) => prev.filter((p) => p !== engChoice));
        setSelectedEsChoices((prev) => prev.filter((p) => p !== spanishChoice));
        setOtherValue('');
      }
    },
    [selectedEngChoices, selectedEsChoices]
  );

  const onSubmittedSelect = useCallback(
    (engChoice: string, spanishChoice: string) => {
      if (engChoice.includes('Exit')) {
        navigate(PATH_ROUTES.PERSONALISED_AI_REPORT);
        return;
      }

      if (engChoice.includes('Initiate Scan')) {
        navigate(
          PATH_ROUTES.START_FACE_SCAN +
            `?redirectTo=${PATH_ROUTES.GENERATE_AI_REPORT}`
        );
        return;
      }

      const isSingleSelect =
        currentQA?.question_type === QuestionType.SINGLE_SELECT;

      onPostQuestionnaire(
        isSingleSelect ? engChoice : JSON.stringify([engChoice]),
        isSingleSelect ? spanishChoice : JSON.stringify([spanishChoice])
      );
    },
    [currentQA]
  );

  const isNotScanPath = useMemo(
    () =>
      currentQA
        ? ['final_msg', 'config', 'no_scan_data'].includes(
            currentQA?.path_type
          ) || currentQA?.question_type === QuestionType.SUBMITTED
        : false,
    [currentQA]
  );

  const saveDisabled = useMemo(
    () =>
      (currentQA?.question_type === QuestionType.TEXT && !userInput) ||
      (currentQA?.question_type === QuestionType.MULTI_SELECT &&
        !selectedEngChoices.length),
    [currentQA, userInput, selectedEngChoices]
  );

  if (isFetching || isPending) {
    return <Loader className='h-[calc(100vh-8rem)]' />;
  }

  if (isFinalMsg) {
    return (
      <FinalReport
        tokenId={currentQA?.token_id || ''}
        retry={currentQA?.retry || 0}
        retryDelay={currentQA?.retryDelay || 5000}
        message={
          locale === 'es'
            ? currentQA?.spanish_question
            : currentQA?.eng_question
        }
      />
    );
  }

  return (
    <div className='mx-4 mt-4 mb-8 sm:mx-12 lg:my-4'>
      <div className='mb-3 lg:hidden'>
        <Button
          variant='outline'
          onClick={() => navigate(-1)}
          className='!h-8 text-blue-500 pr-5 border-blue-500'
        >
          <span>
            <ChevronLeft className='size-4 mb-0.5 mr-0.5' />
          </span>
          {t('back')}
        </Button>
      </div>
      <div className='flex items-center justify-center lg:h-[calc(100vh-8rem)] overflow-hidden'>
        <div className='max-w-md mx-auto space-y-5 text-center lg:space-y-8'>
          <h1 className='text-2xl font-semibold tracking-tight scroll-m-20'>
            {t('ai_report_generation')}
          </h1>
          {isNotScanPath ? null : (
            <div className='mx-2 lg:mx-12'>
              <p className='leading-tight'>{t('ai_report_generation_info')}</p>
            </div>
          )}
          <div className='space-y-2'>
            <ReportQuestion
              question={currentQA}
              userInput={userInput}
              selectedChoices={selectedEngChoices}
              setUserInput={setUserInput}
              onSelect={onSelectMultiChoices}
              onSubmittedSelect={onSubmittedSelect}
              otherValue={otherValue}
              setOtherValue={setOtherValue}
            />
          </div>
          <div className='flex justify-center w-full gap-x-4'>
            {isNotScanPath ? (
              <>
                {isFinalMsg && (
                  <Button
                    variant='outline'
                    className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
                    onClick={() => navigate(PATH_ROUTES.PERSONALISED_AI_REPORT)}
                  >
                    {t('go_back')}
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant='outline'
                className='h-8 px-6 text-gray-700 border-gray-700 rounded-full'
                onClick={onSubmit}
                disabled={saveDisabled}
              >
                {t('save_and_next')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
